/**
 * Single rule row in the rules library list — designed to scan quickly
 * (severity color, status pill, rule-type tag, name, truncated description,
 * tags). Click target opens the detail view.
 */
import * as React from 'react';
import type { RuleListItem } from '../../types/rules';
import { SeverityBadge, StatusPill, RuleTypeBadge, TagChip } from './RuleBadges';

interface RuleCardProps {
  rule: RuleListItem;
  onClick: (_rule: RuleListItem) => void;
}

const MAX_VISIBLE_TAGS = 4;

const RuleCard: React.FC<RuleCardProps> = ({ rule, onClick }) => {
  const visibleTags = rule.tags.slice(0, MAX_VISIBLE_TAGS);
  const extraTagCount = Math.max(0, rule.tags.length - visibleTags.length);

  // PRP-011: when an LLM-generated plain-English render is available, show it
  // as the primary text and demote the technical name to secondary metadata.
  // Fall back to the legacy display when the render is missing.
  const primaryText = rule.plain_english_render || rule.name;
  const secondaryText = rule.plain_english_render
    ? rule.name
    : null;

  return (
    <button
      type="button"
      onClick={() => onClick(rule)}
      className="block w-full rounded-lg border bg-white px-4 py-3 text-left shadow-sm transition hover:border-primary/40 hover:shadow"
    >
      <div className="flex flex-wrap items-center gap-2">
        <SeverityBadge severity={rule.severity} />
        <StatusPill status={rule.status} />
        <RuleTypeBadge ruleType={rule.rule_type} />
        <h3 className="text-sm font-semibold text-slate-900 ml-1 flex-1 min-w-0">
          {primaryText}
        </h3>
      </div>
      {secondaryText && (
        <p className="mt-1 text-xs font-mono text-slate-500">{secondaryText}</p>
      )}
      {!rule.plain_english_render && rule.description && (
        <p className="mt-1 line-clamp-1 text-sm text-slate-600">{rule.description}</p>
      )}
      {(visibleTags.length > 0 || rule.domains.length > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {rule.domains.slice(0, 3).map((d) => (
            <TagChip key={`d-${d}`}>{d}</TagChip>
          ))}
          {visibleTags.map((t) => (
            <TagChip key={`t-${t}`}>{t}</TagChip>
          ))}
          {extraTagCount > 0 && (
            <span className="text-[11px] text-slate-500">+{extraTagCount} more</span>
          )}
        </div>
      )}
    </button>
  );
};

export default RuleCard;
